import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    AlertCircle,
    CheckCircle,
    Loader,
    Play,
    RefreshCw,
    Sparkles,
    X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../context/ToastContext';
import { refineNLPQuery, testRefinedQuery, updateNLPQuery } from '../../services/api';
import { PlainQueryDisplay } from '../ui/PlainQueryDisplay';
import {
    IMPLEMENTATION_STATUS,
    upsertImplementation,
} from '../../utils/reportPlanImplementations';

/**
 * Modal for refining a generated query. In guided mode (report recommendations),
 * refinement starts automatically and the user only accepts or re-refines.
 */
export function QueryRefinementModal({
    open,
    onClose,
    query,
    queryId,
    databaseId,
    initialInstruction = '',
    guided = false,
    reportPlanContext = null,
    onAccepted,
}) {
    const { t } = useTranslation(['query-refinement', 'common']);
    const { showToast } = useToast();
    const [refinementInstruction, setRefinementInstruction] = useState('');
    const [isRefining, setIsRefining] = useState(false);
    const [refinedQueryData, setRefinedQueryData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [refinementFailed, setRefinementFailed] = useState(false);
    const [testParams, setTestParams] = useState({});
    const [isTesting, setIsTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [testResultView, setTestResultView] = useState('table');
    const autoRefineAttemptedRef = useRef(false);

    const initTestParams = useCallback((parameters) => {
        const initial = {};
        if (parameters?.length) {
            parameters.forEach((param) => {
                initial[param.name] = param.default ?? param.example ?? '';
            });
        }
        return initial;
    }, []);

    const resetTestState = useCallback(() => {
        setTestParams({});
        setTestResult(null);
        setTestResultView('table');
        setIsTesting(false);
    }, []);

    const resetState = useCallback(() => {
        setRefinementInstruction('');
        setRefinedQueryData(null);
        setIsRefining(false);
        setIsSaving(false);
        setRefinementFailed(false);
        resetTestState();
        autoRefineAttemptedRef.current = false;
    }, [resetTestState]);

    useEffect(() => {
        if (!open) {
            resetState();
            return;
        }
        setRefinementInstruction(initialInstruction);
        setRefinedQueryData(null);
        autoRefineAttemptedRef.current = false;
    }, [open, initialInstruction, resetState]);

    const runRefinement = useCallback(
        async (instruction) => {
            const trimmed = (instruction || '').trim();
            if (!trimmed || !queryId) return;

            setIsRefining(true);
            setRefinedQueryData(null);
            setRefinementFailed(false);
            resetTestState();
            try {
                const response = await refineNLPQuery({
                    query_id: queryId,
                    instruction: trimmed,
                });

                const result = response.data;
                if (result.status === 'success') {
                    const refinedParams = result.data.parameters || query?.parameters;
                    setRefinedQueryData({
                        refined_query: result.data.refined_query,
                        explanation: result.data.explanation,
                        name: result.data.name || query?.name,
                        description: result.data.description || query?.description,
                        parameters: refinedParams,
                        expected_columns:
                            result.data.expected_columns || query?.expected_columns,
                        tables_involved:
                            result.data.tables_involved || query?.tables_involved || [],
                        validation_query: result.data.validation_query,
                        validation_status: result.data.validation_status || 'pending',
                    });
                    setTestParams(initTestParams(refinedParams));
                    setTestResult(null);
                } else {
                    setRefinementFailed(true);
                    showToast(result.message || t('query-refinement:failedToRefineQuery'), 'error');
                }
            } catch (error) {
                console.error('Error refining query:', error);
                setRefinementFailed(true);
                showToast(
                    error.response?.data?.detail || t('query-refinement:failedToRefineQuery'),
                    'error'
                );
            } finally {
                setIsRefining(false);
            }
        },
        [query, queryId, showToast, initTestParams, resetTestState, t]
    );

    const handleTestRefinedQuery = async () => {
        if (!refinedQueryData || !databaseId) return;

        setIsTesting(true);
        setTestResult(null);
        try {
            const response = await testRefinedQuery({
                database_id: databaseId,
                query: refinedQueryData.refined_query,
                parameters: testParams,
            });
            setTestResult(response.data);
        } catch (error) {
            console.error('Error testing refined query:', error);
            setTestResult({
                error: error.response?.data?.detail || error.message,
            });
        } finally {
            setIsTesting(false);
        }
    };

    useEffect(() => {
        if (
            !open ||
            !guided ||
            !initialInstruction?.trim() ||
            refinedQueryData ||
            isRefining ||
            autoRefineAttemptedRef.current
        ) {
            return;
        }
        autoRefineAttemptedRef.current = true;
        runRefinement(initialInstruction);
    }, [open, guided, initialInstruction, refinedQueryData, isRefining, runRefinement]);

    const handleAccept = async () => {
        if (!refinedQueryData) return;

        setIsSaving(true);
        try {
            const response = await updateNLPQuery(queryId, {
                query: refinedQueryData.refined_query,
                name: refinedQueryData.name,
                description: refinedQueryData.description,
                parameters: refinedQueryData.parameters,
                expected_columns: refinedQueryData.expected_columns,
                tables_involved: refinedQueryData.tables_involved,
            });

            const result = response.data;
            if (result.status !== 'success') {
                showToast(result.message || t('query-refinement:failedToUpdateQuery'), 'error');
                return;
            }

            if (onAccepted) {
                onAccepted(refinedQueryData);
            } else if (reportPlanContext?.reportGenerationId) {
                upsertImplementation(
                    reportPlanContext.reportGenerationId,
                    reportPlanContext.recommendationIndex,
                    reportPlanContext.recommendation,
                    {
                        status: IMPLEMENTATION_STATUS.COMPLETED,
                        query_id: Number(queryId),
                        insight_id: reportPlanContext.recommendation?.insight_id,
                    }
                );
            }

            showToast(t('query-refinement:queryRefinedAndSaved'), 'success');
            onClose();
        } catch (error) {
            console.error('Error updating query:', error);
            showToast(
                error.response?.data?.detail || t('query-refinement:failedToUpdateQuery'),
                'error'
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handleReRefine = () => {
        setRefinedQueryData(null);
        setRefinementFailed(false);
        resetTestState();
        const instruction = refinementInstruction.trim() || initialInstruction.trim();
        if (instruction) {
            runRefinement(instruction);
        }
    };

    if (!open || !query) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto my-8">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white rounded-t-lg z-10">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-primary-600" />
                        <h3 className="text-lg font-semibold text-gray-900">
                            {t('query-refinement:refineQuery')}
                            {query.name ? (
                                <span className="text-gray-500 font-normal">
                                    {' '}
                                    — {query.name}
                                </span>
                            ) : null}
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label={t('common:close')}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    {guided && initialInstruction && (
                        <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                            <h4 className="font-semibold text-amber-900 text-sm mb-2">
                                {t('query-refinement:refinementContext')}
                            </h4>
                            <p className="text-sm text-amber-900/90 whitespace-pre-wrap">
                                {initialInstruction}
                            </p>
                        </div>
                    )}

                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h4 className="font-semibold text-gray-900 text-sm mb-2">
                            {t('query-refinement:originalQuery')}
                        </h4>
                        <PlainQueryDisplay sql={query.query} />
                    </div>

                    {!guided && (
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                                {t('query-refinement:refinementInstructionLabel')}
                            </label>
                            <textarea
                                value={refinementInstruction}
                                onChange={(e) => setRefinementInstruction(e.target.value)}
                                placeholder={t('query-refinement:refinementInstructionPlaceholder')}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm resize-none"
                                rows={4}
                            />
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => runRefinement(refinementInstruction)}
                                    disabled={isRefining || !refinementInstruction.trim()}
                                    className={`px-6 py-2.5 text-white rounded-lg font-semibold flex items-center gap-2 ${
                                        isRefining || !refinementInstruction.trim()
                                            ? 'bg-primary-400 cursor-not-allowed'
                                            : 'bg-primary-600 hover:bg-primary-700'
                                    }`}
                                >
                                    {isRefining ? (
                                        <>
                                            <Loader className="w-4 h-4 animate-spin" />
                                            {t('query-refinement:refining')}
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles className="w-4 h-4" />
                                            {t('query-refinement:refineQuery')}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {guided && isRefining && !refinedQueryData && (
                        <div className="flex flex-col items-center justify-center py-10 gap-3 text-primary-700">
                            <Loader className="w-10 h-10 animate-spin text-primary-600" />
                            <p className="text-sm font-medium">
                                {t('query-refinement:applyingRefinementFromPlan')}
                            </p>
                        </div>
                    )}

                    {guided && !isRefining && !refinedQueryData && !initialInstruction?.trim() && (
                        <div className="flex items-center gap-2 text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
                            <AlertCircle className="w-5 h-5 flex-shrink-0" />
                            {t('query-refinement:noRefinementInstruction')}
                        </div>
                    )}

                    {guided && refinementFailed && !isRefining && !refinedQueryData && (
                        <div className="flex flex-col items-center gap-3 py-4">
                            <p className="text-sm text-red-700">
                                {t('query-refinement:refinementFailed')}
                            </p>
                            <button
                                type="button"
                                onClick={handleReRefine}
                                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg inline-flex items-center gap-2"
                            >
                                <RefreshCw className="w-4 h-4" />
                                {t('common:tryAgain')}
                            </button>
                        </div>
                    )}

                    {refinedQueryData && (
                        <div className="border-t border-gray-200 pt-4 space-y-4">
                            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    <h4 className="font-semibold text-green-900 text-sm">
                                        {t('query-refinement:refinedQuery')}
                                    </h4>
                                </div>
                                {refinedQueryData.explanation && (
                                    <p className="text-xs text-green-700 mb-2">
                                        {refinedQueryData.explanation}
                                    </p>
                                )}
                                <PlainQueryDisplay sql={refinedQueryData.refined_query} />
                            </div>

                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-4">
                                <h4 className="font-semibold text-gray-900 text-sm">
                                    {t('query-refinement:tryRefinedQuery')}
                                </h4>
                                {refinedQueryData.parameters?.length > 0 ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {refinedQueryData.parameters.map((param, idx) => (
                                            <div key={idx}>
                                                <label className="block text-sm font-medium text-slate-800 mb-1.5 lowercase">
                                                    {param.name.replace(/_/g, ' ')}
                                                    {param.description && (
                                                        <span className="block text-xs text-gray-500 mt-0.5 font-normal normal-case">
                                                            {param.description}
                                                        </span>
                                                    )}
                                                </label>
                                                <input
                                                    type={
                                                        param.type === 'number' ||
                                                        param.type === 'integer'
                                                            ? 'number'
                                                            : 'text'
                                                    }
                                                    value={testParams[param.name] ?? ''}
                                                    onChange={(e) =>
                                                        setTestParams((prev) => ({
                                                            ...prev,
                                                            [param.name]: e.target.value,
                                                        }))
                                                    }
                                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                    placeholder={
                                                        param.default ||
                                                        param.example ||
                                                        t('query-refinement:enterParamPlaceholder', { name: param.name })
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-600">
                                        {t('query-refinement:noParametersMessage')}
                                    </p>
                                )}
                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        onClick={handleTestRefinedQuery}
                                        disabled={isTesting || !databaseId}
                                        className={`px-5 py-2 text-white rounded-lg font-semibold text-sm inline-flex items-center gap-2 ${
                                            isTesting || !databaseId
                                                ? 'bg-primary-400 cursor-not-allowed'
                                                : 'bg-primary-600 hover:bg-primary-700'
                                        }`}
                                    >
                                        {isTesting ? (
                                            <>
                                                <Loader className="w-4 h-4 animate-spin" />
                                                {t('query-refinement:running')}
                                            </>
                                        ) : (
                                            <>
                                                <Play className="w-4 h-4" />
                                                {t('query-refinement:testQuery')}
                                            </>
                                        )}
                                    </button>
                                </div>

                                {testResult && (
                                    <div className="border-t border-gray-200 pt-4 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <h5 className="font-medium text-gray-900 text-sm">
                                                {t('query-refinement:testResults')}
                                            </h5>
                                            {!testResult.error && (
                                                <select
                                                    value={testResultView}
                                                    onChange={(e) =>
                                                        setTestResultView(e.target.value)
                                                    }
                                                    className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                >
                                                    <option value="table">{t('query-refinement:table')}</option>
                                                    <option value="json">{t('query-refinement:json')}</option>
                                                </select>
                                            )}
                                        </div>
                                        {testResult.error ? (
                                            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                                                {typeof testResult.error === 'string'
                                                    ? testResult.error
                                                    : JSON.stringify(testResult.error)}
                                            </div>
                                        ) : testResultView === 'table' ? (
                                            <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
                                                <table className="min-w-full text-sm">
                                                    <thead>
                                                        <tr className="border-b border-gray-200 bg-gray-50/80">
                                                            {testResult.data?.rows?.length > 0 &&
                                                                Object.keys(
                                                                    testResult.data.rows[0]
                                                                ).map((key) => (
                                                                    <th
                                                                        key={key}
                                                                        className="text-left py-2 px-3 font-medium text-gray-700"
                                                                    >
                                                                        {key.replace(/_/g, ' ')}
                                                                    </th>
                                                                ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {testResult.data?.rows?.map(
                                                            (row, rowIdx) => (
                                                                <tr
                                                                    key={rowIdx}
                                                                    className="border-b border-gray-100"
                                                                >
                                                                    {Object.values(row).map(
                                                                        (value, colIdx) => (
                                                                            <td
                                                                                key={colIdx}
                                                                                className="py-2 px-3 text-gray-600"
                                                                            >
                                                                                {typeof value ===
                                                                                'number'
                                                                                    ? value.toLocaleString()
                                                                                    : String(
                                                                                          value ??
                                                                                              ''
                                                                                      )}
                                                                            </td>
                                                                        )
                                                                    )}
                                                                </tr>
                                                            )
                                                        )}
                                                    </tbody>
                                                </table>
                                                {testResult.data?.rows?.length > 0 && (
                                                    <p className="text-xs text-gray-500 px-3 py-2 border-t border-gray-100">
                                                        {t('query-refinement:showingRows', { count: testResult.data.rows.length })}
                                                    </p>
                                                )}
                                                {(!testResult.data?.rows ||
                                                    testResult.data.rows.length === 0) && (
                                                    <p className="text-sm text-gray-500 p-4">
                                                        {t('query-refinement:queryRanNoRows')}
                                                    </p>
                                                )}
                                            </div>
                                        ) : (
                                            <pre className="bg-white border border-gray-200 rounded-lg p-3 overflow-x-auto text-xs text-gray-700 max-h-48">
                                                {JSON.stringify(testResult.data, null, 2)}
                                            </pre>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={handleReRefine}
                                    disabled={isRefining || isSaving}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors inline-flex items-center gap-2 disabled:opacity-50"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                    {t('query-refinement:reRefine')}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleAccept}
                                    disabled={isRefining || isSaving || isTesting}
                                    className={`px-6 py-2 text-white rounded-lg font-semibold inline-flex items-center gap-2 ${
                                        isSaving || isTesting
                                            ? 'bg-green-400 cursor-not-allowed'
                                            : 'bg-green-600 hover:bg-green-700'
                                    }`}
                                >
                                    {isSaving ? (
                                        <>
                                            <Loader className="w-4 h-4 animate-spin" />
                                            {t('common:saving')}
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle className="w-4 h-4" />
                                            {t('query-refinement:accept')}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export function buildRefinementInstruction(recommendation) {
    if (!recommendation) return '';
    const parts = [];
    if (recommendation.blocking_reason?.trim()) {
        parts.push(recommendation.blocking_reason.trim());
    }
    if (recommendation.instruction?.trim()) {
        parts.push(recommendation.instruction.trim());
    }
    return parts.join('\n\n');
}
